/**
 * useMenu Hook
 * Handles menu modal state and sign out logic
 */

import { useState, useCallback } from 'react';
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';
import { useRouter } from 'expo-router';
import { useDialog } from '../../shared/hooks/useDialog';
import { PUBLIC_WELCOME_PATH } from '../../signIn/constants/publicAuthRoutes.constants';

interface UseMenuReturn {
  menuVisible: boolean;
  setMenuVisible: (visible: boolean) => void;
  handleMenuPress: () => void;
  handleSignOut: () => Promise<void>;
}

export const useMenu = (): UseMenuReturn => {
  const [menuVisible, setMenuVisible] = useState(false);
  const { signOut } = useSupabaseAuth();
  const router = useRouter();
  const { showDialog, showAlert } = useDialog();

  const handleMenuPress = useCallback(() => {
    setMenuVisible(true);
  }, []);

  const handleSignOut = useCallback(async () => {
    const confirmed = await showDialog({
      title: 'Sign Out',
      message: 'Are you sure you want to sign out?',
      type: 'danger',
      confirmText: 'Sign Out',
      cancelText: 'Cancel',
    });

    if (!confirmed) return;

    try {
      await signOut();
      // Land on the guest-first Welcome front door, not the sign-in form (guest-first AC-4b).
      router.replace(PUBLIC_WELCOME_PATH);
    } catch {
      await showAlert({
        title: 'Error',
        message: 'Failed to sign out. Please try again.',
        type: 'danger',
      });
    }
  }, [signOut, router, showDialog, showAlert]);

  return {
    menuVisible,
    setMenuVisible,
    handleMenuPress,
    handleSignOut,
  };
};

